import React, { useState, useEffect } from 'react';
import { authApi } from '../services/api';

export default function AuthModal({ isOpen, onClose, onLogin }) {
  const [step,      setStep]      = useState('phone'); // 'phone' | 'whatsapp-join' | 'otp'
  const [phone,     setPhone]     = useState('');
  const [otp,       setOtp]       = useState('');
  const [devOtp,    setDevOtp]    = useState('');
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState('');
  const [countdown, setCountdown] = useState(0);
  const [joinCode,  setJoinCode]  = useState('');
  const [waFrom,    setWaFrom]    = useState('');
  const [joinSent,  setJoinSent]  = useState(false);
  const [otpReady,  setOtpReady]  = useState(false);
  const [isNewUser,    setIsNewUser]    = useState(true);
  const [whatsappOtp,  setWhatsappOtp]  = useState(false);

  useEffect(() => {
    if (isOpen) {
      setStep('phone'); setPhone(''); setOtp('');
      setDevOtp(''); setError(''); setCountdown(0);
      setJoinCode(''); setWaFrom(''); setJoinSent(false); setOtpReady(false); setIsNewUser(true); setWhatsappOtp(false);
    }
  }, [isOpen]);

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  useEffect(() => {
    if (countdown <= 0) return;
    const t = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown]);

  const normalize = (p) => p.replace(/[^\d]/g, '').replace(/^91(\d{10})$/, '$1');

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setError('');
    const norm = normalize(phone);
    if (norm.length !== 10) { setError('Enter a valid 10-digit mobile number'); return; }
    setLoading(true);
    try {
      const res = await authApi.sendOtp(norm);
      if (res.whatsappFrom) {
        // New user — show join sandbox step first
        setIsNewUser(true);
        setJoinCode(res.sandboxJoinCode || '');
        setWaFrom((res.whatsappFrom || '').replace('whatsapp:', ''));
        setStep('whatsapp-join');
      } else if (res.otpSent) {
        // Existing user — OTP already sent via WhatsApp by backend
        setIsNewUser(false);
        setWhatsappOtp(true);
        setStep('otp');
        setCountdown(30);
      } else {
        // Twilio not configured — fallback OTP
        setDevOtp(res.otp || '');
        setStep('otp');
        setCountdown(30);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError('');
    if (otp.length !== 6) { setError('Enter the 6-digit OTP'); return; }
    setLoading(true);
    try {
      const data = await authApi.verifyOtp(normalize(phone), otp);
      onLogin(data);
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleJoinSent = async (checked) => {
    if (otpReady) return; // already initiated
    setJoinSent(checked);
    if (!checked) return;
    setError('');
    setLoading(true);
    try {
      await authApi.initiateWhatsappSignup(normalize(phone));
      setOtpReady(true);
    } catch (err) {
      setError(err.message);
      setJoinSent(false);
    }
    setLoading(false);
  };

  if (!isOpen) return null;

  return (
    <div className="auth-modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="auth-modal-box">
        <button className="auth-modal-close" onClick={onClose} aria-label="Close">X</button>

        <div className="auth-modal-logo">
          <img
            src="https://firebasestorage.googleapis.com/v0/b/daycare-39d32.firebasestorage.app/o/images%2Flogo_la.png?alt=media"
            alt="LittleAura"
            className="auth-logo-img"
          />
          <div>
            <div className="auth-modal-logo-name">Little<span> Aura</span></div>
            <div className="auth-modal-logo-sub">A Second Home for Every Child</div>
          </div>
        </div>

        {/* ── STEP 1: Phone ── */}
        {step === 'phone' && (
          <form onSubmit={handleSendOtp}>
            <p className="auth-modal-hint">Enter your mobile number to receive a one-time password</p>
            <div className="form-group">
              <label className="form-label">Mobile Number</label>
              <div className="otp-phone-wrap">
                <span className="otp-phone-prefix">+91</span>
                <input
                  className="form-input otp-phone-input"
                  type="tel"
                  inputMode="numeric"
                  placeholder="98765 43210"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/[^\d]/g, '').slice(0, 10))}
                  required
                  autoFocus
                />
              </div>
            </div>
            {error && <div className="form-error">{error}</div>}
            <button className="btn btn-primary btn-full btn-lg mt-16" type="submit" disabled={loading}>
              {loading ? 'Checking…' : 'Continue'}
            </button>
            <p className="auth-modal-hint" style={{ marginTop: 16, fontSize: 12 }}>
              Schools and Admins are recognised by their registered number.
            </p>
          </form>
        )}

        {/* ── STEP 2a: WhatsApp Join (new parent) ── */}
        {step === 'whatsapp-join' && (() => {
          const joinMessage = joinCode.startsWith('join ') ? joinCode : `join ${joinCode}`;
          return (
          <div>
            <p className="auth-modal-hint" style={{ marginBottom: 8 }}>
              <strong>Complete WhatsApp Verification</strong>
            </p>
            <p style={{ fontSize: 13, color: '#4b5563', marginBottom: 16 }}>
              {isNewUser
                ? "You're signing up for the first time. Send the message below to our WhatsApp number to verify your identity:"
                : 'Send the message below to our WhatsApp number to verify and login:'}
            </p>

            {/* WhatsApp number */}
            <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 13 }}>
              <div style={{ color: '#6b7280', fontSize: 11, marginBottom: 2 }}>WhatsApp Number</div>
              <div style={{ fontWeight: 700, fontSize: 16, color: '#166534' }}>📱 {waFrom}</div>
            </div>

            {/* Join code */}
            <div style={{ background: '#fefce8', border: '1px solid #fde047', borderRadius: 8, padding: '12px 14px', marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ color: '#6b7280', fontSize: 11, marginBottom: 2 }}>Send this message</div>
                <div style={{ fontFamily: 'monospace', fontSize: 20, fontWeight: 700, color: '#92400e', letterSpacing: 2 }}>{joinMessage}</div>
              </div>
              <button type="button"
                onClick={() => navigator.clipboard?.writeText(joinMessage)}
                style={{ fontSize: 11, padding: '4px 10px', border: '1px solid #d97706', borderRadius: 6, background: 'white', color: '#d97706', cursor: 'pointer', fontWeight: 600 }}>
                Copy
              </button>
            </div>

            {/* WhatsApp deep link */}
            <a
              href={`https://wa.me/${waFrom.replace(/[^+\d]/g, '')}?text=${encodeURIComponent(joinMessage)}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ display: 'block', textAlign: 'center', marginBottom: 16, fontSize: 13, color: '#16a34a', fontWeight: 600, textDecoration: 'none' }}>
              📲 Open WhatsApp &amp; Send
            </a>

            {/* Checkbox */}
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', marginBottom: 16 }}>
              <input type="checkbox" checked={joinSent} disabled={loading || otpReady}
                onChange={e => handleJoinSent(e.target.checked)}
                style={{ marginTop: 3, width: 16, height: 16, accentColor: '#7c3aed', cursor: 'pointer', flexShrink: 0 }}
              />
              <span style={{ fontSize: 14, color: '#374151' }}>I have sent the message to the WhatsApp number above</span>
            </label>

            {loading && !otpReady && (
              <div style={{ textAlign: 'center', color: '#6b7280', fontSize: 13, marginBottom: 12 }}>
                <div className="home-spinner" style={{ width: 20, height: 20, display: 'inline-block', marginRight: 8 }} />
                {isNewUser ? 'Creating your account and sending OTP…' : 'Sending OTP to your WhatsApp…'}
              </div>
            )}

            {otpReady && (
              <form onSubmit={handleVerifyOtp}>
                <p className="auth-modal-hint" style={{ color: '#16a34a', marginBottom: 10 }}>
                  ✅ OTP sent to your WhatsApp. Enter it below:
                </p>
                <div className="form-group">
                  <label className="form-label">6-digit OTP</label>
                  <input
                    className="form-input otp-input"
                    type="tel"
                    inputMode="numeric"
                    placeholder="------"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    maxLength={6}
                    autoFocus
                  />
                </div>
                {error && <div className="form-error">{error}</div>}
                <button className="btn btn-primary btn-full btn-lg mt-16" type="submit"
                  disabled={loading || otp.length !== 6}>
                  {loading ? 'Verifying…' : 'Verify & Login'}
                </button>
              </form>
            )}

            {!otpReady && error && <div className="form-error">{error}</div>}
            <button type="button" className="auth-modal-link"
              style={{ marginTop: 16, display: 'block', textAlign: 'center', fontSize: 12 }}
              onClick={() => { setStep('phone'); setJoinSent(false); setOtpReady(false); setOtp(''); setError(''); }}>
              ← Change number
            </button>
          </div>
          );
        })()}

        {/* ── STEP 2b: OTP ── */}
        {step === 'otp' && (
          <form onSubmit={handleVerifyOtp}>
            <p className="auth-modal-hint">
              {whatsappOtp
                ? <>OTP sent to your <strong>WhatsApp</strong> (+91 {normalize(phone)})</>
                : <>OTP sent to <strong>+91 {normalize(phone)}</strong></>}
            </p>
            {devOtp && !whatsappOtp && (
              <div className="otp-dev-hint">Your OTP: <strong>{devOtp}</strong></div>
            )}
            <div className="form-group">
              <label className="form-label">Enter 6-digit OTP</label>
              <input
                className="form-input otp-input"
                type="tel"
                inputMode="numeric"
                placeholder="------"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                maxLength={6}
                required
                autoFocus
              />
            </div>
            {error && <div className="form-error">{error}</div>}
            <button
              className="btn btn-primary btn-full btn-lg mt-16"
              type="submit"
              disabled={loading || otp.length !== 6}
            >
              {loading ? 'Verifying...' : 'Verify and Login'}
            </button>
            <div style={{ textAlign: 'center', marginTop: 14, fontSize: 13, color: '#6b7280' }}>
              {countdown > 0 ? (
                <span>Resend OTP in {countdown}s</span>
              ) : (
                <button
                  type="button"
                  className="auth-modal-link"
                  onClick={() => { setStep('phone'); setOtp(''); setError(''); setDevOtp(''); }}
                >
                  Change number / Resend OTP
                </button>
              )}
            </div>
          </form>
        )}
      </div>
    </div>
  );
}